import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@api';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import FinalScoreEditor from './FinalScoreEditor';
import EditGameDetailsModal from './EditGameDetailsModal';
import GameActionButtons from "./GameActionButtons";
import GameInfoSection from "./GameInfoSection";
import TeamPlayerStatsTable from "./TeamPlayerStatsTable";
import TennisTeamMatchEditor from "./TennisTeamMatchEditor";
import RacquetTeamStatsSummary from "./RacquetTeamStatsSummary";
import './GameAuth.css';

function GameAuth() {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editModeScores, setEditModeScores] = useState(false);
  const [periodScores, setPeriodScores] = useState([]);
  const [racquetMatches, setRacquetMatches] = useState([]);
  const [statValues, setStatValues] = useState({});
  const [stats, setStats] = useState([]);  // Add stats state
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  const [editFinalScores, setEditFinalScores] = useState(false); // New state 
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    video_url: '',
    location: '',
    location_url: '',
    date: '',
    time: '',
    // status: ''
  });
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [finalScoreForm, setFinalScoreForm] = useState({
    score_home: 0,
    score_away: 0,
  });
  const [isSaving, setIsSaving] = useState(false)
  const [originalStatValues, setOriginalStatValues] = useState({});
  const [originalFinalScoreForm, setOriginalFinalScoreForm] = useState({ score_home: 0, score_away: 0 });

  const sportName =
    game?.sport?.type ||
    game?.seasons_league?.league?.sportsv2?.type ||
    game?.seasons_league?.league?.SportV2?.type ||
    game?.seasons_league?.league?.sportv2?.type ||
    game?.seasons_league?.league?.sport?.type ||
    "";
  const normalizedSportName = sportName.toLowerCase();

  const isRacquetSport =
    normalizedSportName === "tennis" ||
    normalizedSportName === "pickleball";

  const usesPlayerStatsTable =
    normalizedSportName === "basketball" ||
    normalizedSportName === "volleyball";


  const { id } = useParams();
  const fetchGame = async () => {
    try {
      const res = await api.get(`/api/gamesv2/${id}`);

      const { game, homeRoster, awayRoster, periodScores, allStats, playerStats } = res.data;
      console.log("ALL STATS:", allStats);
      const sortedStats = (allStats || [])
        .filter(stat => !stat.hidden)
        .sort((a, b) => a.order - b.order);
      const getPlayerName = (player) => {
        return (
          player?.name ||
          player?.full_name ||
          `${player?.firstName || ""} ${player?.lastName || ""}`.trim() ||
          `${player?.first_name || ""} ${player?.last_name || ""}`.trim() ||
          player?.username ||
          "Unknown Player"
        );
      };

      const isIndividualGame = game.home_player_id || game.away_player_id;

      const homePlayers = isIndividualGame
        ? [
          game.home_player?.player
            ? {
              ...game.home_player.player,
              roster_id: null,
            }
            : null,
          game.home_partner_player?.player
            ? {
              ...game.home_partner_player.player,
              roster_id: null,
            }
            : null,
        ].filter(Boolean)
        : homeRoster?.map((r) => ({
          ...r.player,
          roster_id: r.id,
        })) || [];

      const awayPlayers = isIndividualGame
        ? [
          game.away_player?.player
            ? {
              ...game.away_player.player,
              roster_id: null,
            }
            : null,
          game.away_partner_player?.player
            ? {
              ...game.away_partner_player.player,
              roster_id: null,
            }
            : null,
        ].filter(Boolean)
        : awayRoster?.map((r) => ({
          ...r.player,
          roster_id: r.id,
        })) || [];


      const transformedGame = {
        ...game,
        isIndividualGame,
        sport:
          game?.sport ||
          game?.seasons_league?.league?.sportv2 ||
          game?.seasons_league?.league?.SportV2 ||
          null,
        homeTeam: isIndividualGame
          ? {
            id: game.home_player_id,
            name:
              game.match_format === "doubles"
                ? `${getPlayerName(game.home_player?.player)} / ${getPlayerName(
                  game.home_partner_player?.player
                )}`
                : getPlayerName(game.home_player?.player),
            players: homePlayers,
          }
          : { ...game.home_team?.team, players: homePlayers },
        awayTeam: isIndividualGame
          ? {
            id: game.away_player_id, name:
              game.match_format === "doubles"
                ? `${getPlayerName(game.away_player?.player)} / ${getPlayerName(
                  game.away_partner_player?.player
                )}`
                : getPlayerName(game.away_player?.player),
            players: awayPlayers,
          }
          : { ...game.away_team?.team, players: awayPlayers },
      };

      setGame(transformedGame);
      const organizationId =
        game.seasons_league?.season?.organization_id;

      if (organizationId) {
        try {
          const registrationRes = await api.get(
            "/api/player-registrations",
            {
              params: {
                organization_id: organizationId,
                season_id: game.seasons_league?.season_id,
              },
              withCredentials: true,
            }
          );

          const statuses = {};

          registrationRes.data.players.forEach((player) => {
            statuses[player.id] = player.is_registered;
          });

          setPlayerRegistrations(statuses);
        } catch (err) {
          console.error("Error loading registrations:", err);
        }
      }
      setFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });

      setStats(sortedStats);

      // 🧠 Group period scores into one per period with both team scores
      const groupedPeriodScores = [];
      for (const score of periodScores) {
        const existing = groupedPeriodScores.find(p => p.gamePeriod?.id === score.gamePeriod?.id);
        if (!existing) {
          groupedPeriodScores.push({
            id: score.id, // pick one score ID (any)
            gamePeriod: score.gamePeriod,
            period_score_team1: score.team_id === game.home_team_id ? score.score : null,
            period_score_team2: score.team_id === game.away_team_id ? score.score : null,
          });
        } else {
          if (score.team_id === game.home_team_id) existing.period_score_team1 = score.score;
          if (score.team_id === game.away_team_id) existing.period_score_team2 = score.score;
        }
      }

      setPeriodScores(groupedPeriodScores);

      // 🧠 Build player stat values
      const values = {};
      playerStats?.forEach((stat) => {
        const key = stat.roster_id
          ? `${stat.roster_id}-${stat.stat_id}`
          : `${stat.player_id}-${stat.stat_id}`; // fallback for old data
        values[key] = stat.value;
      });


      setStatValues(values);
      setOriginalStatValues(values); // ✅ save original stats

      setFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });
      setOriginalFinalScoreForm({
        score_home: game.score_home ?? 0,
        score_away: game.score_away ?? 0,
      });
      const ptsStatId = sortedStats.find(stat => stat.abbreviation === "PTS")?.id;

      const totalPointsHome = homePlayers.reduce((sum, player) => {
        return sum + (values[`${player.roster_id}-${ptsStatId}`] || 0);
      }, 0);

      const totalPointsAway = awayPlayers.reduce((sum, player) => {
        return sum + (values[`${player.roster_id}-${ptsStatId}`] || 0);
      }, 0);

      const mismatchDetected =
        totalPointsHome !== (game.score_home ?? 0) ||
        totalPointsAway !== (game.score_away ?? 0);

      sessionStorage.setItem(`mismatch-${game.id}`, JSON.stringify(mismatchDetected));

    } catch (err) {
      console.error("Error fetching game:", err);
    } finally {
      setLoading(false);
    }
  };


  const fetchRacquetMatchesForSummary = async () => {
    if (!id) return;

    try {
      const res = await api.get(`/api/gamesv2/${id}/racquet-matches`);
      setRacquetMatches(res.data || []);
    } catch (error) {
      console.error("Error fetching racquet matches for summary:", error);
    }
  };


  useEffect(() => {
    fetchGame();
    fetchRacquetMatchesForSummary();
  }, [id]);



  // Initialize stat values from existing player stats
  const initializeStatValues = (data) => {
    if (!data || !data.playerStats) return;
    const values = {};
    data.playerStats.forEach((stat) => {
      values[`${stat.player_id}-${stat.stat_id}`] = stat.value;
    });
    setStatValues(values);
  };

  // Handle stat input change
  const handleInputChange = (roster_id, stat_id, value) => {
    setStatValues((prev) => ({
      ...prev,
      [`${roster_id}-${stat_id}`]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!game || !game.id) {
      alert("Game ID is missing. Cannot update stats.");
      return;
    }
    const getVal = (player, abbr) =>
      Number(statValues[`${player.roster_id}-${getStatId(abbr)}`] || 0);

    const ftmStatId = getStatId("FTM");
    const ftaStatId = getStatId("FTA");
    const twoPMStatId = getStatId("2PM");
    const twoPAStatId = getStatId("2PA");
    const threePMStatId = getStatId("3PM");
    const threePAStatId = getStatId("3PA");


    for (const player of [...game.homeTeam.players, ...game.awayTeam.players]) {
      const ftm = getVal(player, "FTM");
      const fta = getVal(player, "FTA");
      const twoPM = getVal(player, "2PM");
      const twoPA = getVal(player, "2PA");
      const threePM = getVal(player, "3PM");
      const threePA = getVal(player, "3PA");

      const fullName = `${player.firstName || ""} ${player.lastName || ""}`
        .replace(/[\s\u00A0]+/g, " ")
        .trim();

      if (ftmStatId && ftaStatId && ftm > fta) {
        alert(`${fullName}: FTM cannot be greater than FTA.`);
        return;
      }

      if (twoPMStatId && twoPAStatId && twoPM > twoPA) {
        alert(`${fullName}: 2PM cannot be greater than 2PA.`);
        return;
      }

      if (threePMStatId && threePAStatId && threePM > threePA) {
        alert(`${fullName}: 3PM cannot be greater than 3PA.`);
        return;
      }
    }
    // 🛑 Prevent same player from having stats for both teams in the same game (via roster_id)
    const playerRosterMap = {};

    for (const key of Object.keys(statValues)) {
      const [entity_id, stat_id] = key.split('-').map(Number);
      const value = statValues[key];
      if (value === 0 || value === "" || value == null) continue; // skip blanks

      // Find player by roster_id first, fallback to player_id
      const player = [...game.homeTeam.players, ...game.awayTeam.players]
        .find(p => p.roster_id === entity_id || p.id === entity_id);
      if (!player) continue;

      // Determine which team this roster belongs to
      const team =
        game.homeTeam.players.some(p => p.roster_id === player.roster_id)
          ? "home"
          : game.awayTeam.players.some(p => p.roster_id === player.roster_id)
            ? "away"
            : null;

      if (!playerRosterMap[player.id]) {
        playerRosterMap[player.id] = team;
      } else if (playerRosterMap[player.id] !== team) {
        alert(`⚠️ Cannot save stats: ${player.firstName} ${player.lastName} has stats recorded for both teams in this game.`);
        return; // ⛔ stop submission immediately
      }
    }

    // Get the actual stat_id for PTS (points)
    const ptsStatId = stats.find(stat => stat.abbreviation === "PTS")?.id;

    const totalPointsHome = game.homeTeam?.players.reduce((sum, player) => {
      return sum + (statValues[`${player.roster_id}-${ptsStatId}`] || 0);
    }, 0);

    const totalPointsAway = game.awayTeam?.players.reduce((sum, player) => {
      return sum + (statValues[`${player.roster_id}-${ptsStatId}`] || 0);
    }, 0);

    const statsToUpdate = [];
    Object.keys(statValues).forEach((key) => {
      const [entity_id, stat_id] = key.split('-').map(Number);

      // Try to match by roster_id first, fallback to player_id
      const player = [...game.homeTeam.players, ...game.awayTeam.players]
        .find(p => p.roster_id === entity_id || p.id === entity_id);

      statsToUpdate.push({
        player_id: player?.id,
        roster_id: player?.roster_id || null,
        game_id: game.id,
        stat_id,
        value: statValues[key] || 0,
      });
    });

    setIsSaving(true);
    try {
      await api.put('/api/player-game-stats', {
        player_id: statsToUpdate[0]?.player_id,
        game_id: game.id,
        stats: statsToUpdate,
      });

      await fetchGame();
      setEditMode(false);
    } catch (error) {
      console.error('❌ Error updating stats:', error.response?.data || error);
      alert('Failed to update stats.');
    } finally {
      setIsSaving(false);
    }
  };



  // const handleSaveScores = async () => {
  //   try {
  //     const finalPeriod = periodScores.find(p => p.gamePeriod?.name === "Final");
  //     const finalScoreTeam1 = finalPeriod?.period_score_team1 ?? 0;
  //     const finalScoreTeam2 = finalPeriod?.period_score_team2 ?? 0;

  //     // 1. Save period scores
  //     await api.put(`/api/gamesv2/${game.id}/period-scores`, {
  //       game_id: game.id,
  //       scores: periodScores.map(period => ({
  //         id: period.id,
  //         period_score_team1: period.period_score_team1,
  //         period_score_team2: period.period_score_team2,
  //       })),
  //     });

  //     // 2. Also update final score in GameV2
  //     await api.put(`/api/gamesv2/${game.id}/scores`, {
  //       score_home: finalScoreTeam1,
  //       score_away: finalScoreTeam2,
  //     });

  //     alert("Scores updated successfully!");
  //     setEditModeScores(false);
  //     setEditFinalScores(false);
  //     fetchGame();
  //   } catch (error) {
  //     console.error("Error updating scores:", error);
  //     alert("Failed to update scores.");
  //   }
  // };

  // ✅ move this outside of handleSubmit
  const handleSaveFinalScore = async () => {

    try {
      await api.put(`/api/gamesv2/${game.id}/scores`, {
        score_home: finalScoreForm.score_home,
        score_away: finalScoreForm.score_away,
      });
      alert("Final game score updated!");
      setEditFinalScores(false);
      await fetchGame(); // refresh data
    } catch (err) {
      console.error("Error updating final score:", err);
      alert("Failed to update final score.");
    }
  };


  const handleUpdateGameDetails = async (e) => {
    e.preventDefault();

    // Convert video URL to embed format
    let videoUrl = formData.video_url;
    if (videoUrl.includes('youtube.com')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0]; // Extract video ID
      videoUrl = `https://www.youtube.com/embed/${videoId}`; // Convert to embed URL
    }

    try {
      const { location, location_url, time, status, date } = formData;

      await api.put(`/api/gamesv2/${game.id}/details`, {
        video_url: videoUrl || null,
        location: location || null,
        location_url: location_url || null,
        date: date || null,
        time: time || null,
        status,
      });

      alert("Game details updated successfully!");
      setShowModal(false);
      fetchGame();
    } catch (error) {
      console.error("Error updating game details:", error);
      alert("Failed to update game details.");
    }
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "TBD";
    const date = new Date(dateTime);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };





  const handleScoreChange = (periodId, team, value) => {
    setPeriodScores((prevScores) =>
      prevScores.map((period) =>
        period.id === periodId
          ? {
            ...period,
            [`period_score_${team === "team1" ? "team1" : "team2"}`]: value ? parseInt(value, 10) : 0,
          }
          : period
      )
    );
  };
  const allPeriodsHidden = Object.values(periodScores).every(period => period.gamePeriod?.hidden);


  // Find the Final period's score
  const finalPeriod = periodScores.find(period => period.gamePeriod.name === "Final");

  // Extract the Final period score for team 1
  const finalScoreTeam1 = finalPeriod ? finalPeriod.period_score_team1 : game?.score_team1;
  const finalScoreTeam2 = finalPeriod ? finalPeriod.period_score_team2 : game?.score_team2;
  const getStatId = (abbreviation) => stats.find(s => s.abbreviation === abbreviation)?.id;



  const has2PM = !!getStatId('2PM');
  const has3PM = !!getStatId('3PM');
  const has2PA = !!getStatId('2PA');
  const has3PA = !!getStatId('3PA');

  const allowFGMInput = !(has2PM || has3PM);
  const allowFGAInput = !(has2PA || has3PA);

  const displayDate = game?.date
    ? format(new Date(`${game.date}T00:00:00`), "MMM d, yyyy")
    : game?.game_datetime
      ? format(new Date(game.game_datetime), "MMM d, yyyy")
      : "TBD";

  const displayTime = game?.time
    ? format(new Date(`1970-01-01T${game.time}`), "h:mm a")
    : game?.game_datetime
      ? formatTime(game.game_datetime)
      : "TBD";

  const displayLocation = game?.location || "TBD";
  useEffect(() => {
    if (!editMode) {
      setActivePlayerId(null);
    }
  }, [editMode]);

  if (loading) return null;
  if (!game) return <p>Game not found.</p>;


  return (
    <div className="game-container">
      {isSaving && (
        <div className="saving-overlay">
          <div className="saving-row">
            <div className="saving-text">Saving stats...</div>
            <div className="basketball">
              <div className="ball">
                <div className="lines"></div>
              </div>
              <div className="shadow"></div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`game-auth-page-shell ${isRacquetSport ? "game-auth-page-shell-racquet" : "game-auth-page-shell-standard"
          }`}
      >
        <section className="game-auth-header-card">
          <div className="game-auth-header-main">
            <GameInfoSection game={game} />
          </div>

          <div className="game-auth-header-actions">
            <GameActionButtons
              editMode={editMode}
              setEditMode={setEditMode}
              editFinalScores={editFinalScores}
              setEditFinalScores={setEditFinalScores}
              setShowModal={setShowModal}
              setFormData={setFormData}
              game={game}
              originalStatValues={originalStatValues}
              setStatValues={setStatValues}
              originalFinalScoreForm={originalFinalScoreForm}
              setFinalScoreForm={setFinalScoreForm}
              usesPlayerStatsTable={usesPlayerStatsTable}
              isRacquetSport={isRacquetSport}
              handleSubmit={handleSubmit}
              isSaving={isSaving}
            />
          </div>
        </section>

        <section
          className={`game-auth-content-layout ${isRacquetSport
            ? "game-auth-content-layout-racquet"
            : "game-auth-content-layout-standard"
            }`}
        >
          <div className="game-auth-left-column">
            <section className="game-auth-score-card">
              <div className="game-auth-score-team game-auth-score-team-home">
                {game?.homeTeam?.name || "Home Team"}
              </div>

              <div className="game-auth-score-center">
                {editFinalScores ? (
                  <FinalScoreEditor
                    game={game}
                    finalScoreForm={finalScoreForm}
                    setFinalScoreForm={setFinalScoreForm}
                    handleSaveFinalScore={handleSaveFinalScore}
                    onCancel={() => {
                      setFinalScoreForm(originalFinalScoreForm);
                      setEditFinalScores(false);
                    }}
                  />
                ) : (
                  <>
                    <span className="game-auth-score-number">
                      {finalScoreForm.score_home ?? game?.score_home ?? 0}
                    </span>
                    <span className="game-auth-score-vs">vs</span>
                    <span className="game-auth-score-number">
                      {finalScoreForm.score_away ?? game?.score_away ?? 0}
                    </span>
                  </>
                )}
              </div>

              <div className="game-auth-score-team game-auth-score-team-away">
                {game?.awayTeam?.name || "Away Team"}
              </div>
            </section>

            {usesPlayerStatsTable && (
              <div className="game-auth-main-panel game-auth-main-panel-full">
                <TeamPlayerStatsTable
                  game={game}
                  playerRegistrations={playerRegistrations}
                  stats={stats}
                  statValues={statValues}
                  editMode={editMode}
                  activePlayerId={activePlayerId}
                  setActivePlayerId={setActivePlayerId}
                  handleInputChange={handleInputChange}
                  finalScoreForm={finalScoreForm}
                  getStatId={getStatId}
                  allowFGMInput={allowFGMInput}
                  allowFGAInput={allowFGAInput}
                />
              </div>
            )}

            {isRacquetSport && (
              <div className="game-auth-main-panel game-auth-racquet-lineup-panel">
                <TennisTeamMatchEditor
                  game={game}
                  stats={stats}
                  refreshGame={fetchGame}
                  matches={racquetMatches}
                  setMatches={setRacquetMatches}
                  refreshRacquetMatches={fetchRacquetMatchesForSummary}
                />
              </div>
            )}
          </div>

          {isRacquetSport && (
            <aside className="game-auth-right-column">
              {/* <RacquetTeamStatsSummary
                game={game}
                matches={racquetMatches}
                stats={stats}
              /> */}
            </aside>
          )}
        </section>
      </div>

      {showModal && (
        <EditGameDetailsModal
          formData={formData}
          setFormData={setFormData}
          handleUpdateGameDetails={handleUpdateGameDetails}
          setShowModal={setShowModal}
        />
      )}

    </div>
  );
}

export default GameAuth;

